import { Box, Typography, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Demo1 from './Demo1Form';
import Demo2 from './Demo2Map';
import { RdvInfoTooltip } from './RichTooltip';
import { PRIMARY, ACCENT_GREEN } from './howItWorksConstants';

// Tick vert / croix grise : icône blanche dans un container arrondi de couleur.
const StatusIcon = ({ ok }: { ok: boolean }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      bgcolor: ok ? ACCENT_GREEN : '#cbd5e1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {ok ? (
      <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
    ) : (
      <CloseIcon sx={{ fontSize: 14, color: '#fff' }} />
    )}
  </Box>
);

// Valeur de cellule : Oui/Non → icône, sinon texte brut.
const CellValue = ({
  value,
  color,
  strong,
}: {
  value: string;
  color: string;
  strong?: boolean;
}) => {
  if (value === 'Oui') {
    return <StatusIcon ok />;
  }
  if (value === 'Non') {
    return <StatusIcon ok={false} />;
  }
  return (
    <Typography sx={{ fontSize: 13, color, fontWeight: strong ? 600 : 500 }}>{value}</Typography>
  );
};

const Cell = ({
  children,
  header,
  highlight,
  bottom,
  sticky,
}: {
  children?: React.ReactNode;
  header?: boolean;
  highlight?: boolean;
  bottom?: boolean;
  sticky?: boolean;
}) => {
  const bg = header
    ? highlight
      ? `${PRIMARY}0d`
      : '#f8fafc'
    : highlight
    ? `${PRIMARY}05`
    : '#fff';
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.2,
        borderBottom: bottom ? 'none' : '1px solid #f1f5f9',
        borderRight: '1px solid #f1f5f9',
        bgcolor: bg,
        fontSize: header ? 12 : 13,
        fontWeight: header ? 700 : 500,
        color: highlight && header ? PRIMARY : header ? '#475569' : '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        gap: 0.6,
        position: sticky ? 'sticky' : 'static',
        left: sticky ? 0 : 'auto',
        zIndex: sticky ? 1 : 0,
        boxShadow: sticky ? { xs: '2px 0 4px rgba(0,0,0,0.04)', md: 'none' } : 'none',
      }}
    >
      {children}
    </Box>
  );
};

const RowCells = ({
  row,
  last,
  onRdvInfo,
}: {
  row: { label: string; free: string; auto: string; info?: string; bold?: boolean };
  last: boolean;
  onRdvInfo?: () => void;
}) => (
  <>
    <Cell bottom={last} sticky>
      {row.info ? (
        <RdvInfoTooltip intro={row.info} onOpenHowItWorks={onRdvInfo}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: '#475569',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textUnderlineOffset: 3,
              cursor: 'help',
            }}
          >
            {row.label}
          </Typography>
        </RdvInfoTooltip>
      ) : (
        <Typography sx={{ fontSize: 12, fontWeight: row.bold ? 800 : 600, color: '#475569' }}>
          {row.label}
        </Typography>
      )}
    </Cell>
    <Cell bottom={last}>
      <CellValue value={row.free} color="#64748b" />
    </Cell>
    <Cell bottom={last} highlight>
      <CellValue value={row.auto} color="#1a1a1a" strong />
    </Cell>
  </>
);

const ComparisonTable = ({ onRdvInfo }: { onRdvInfo?: () => void }) => {
  const rows = [
    { label: "Accès à l'application", free: 'Oui', auto: 'Oui' },
    { label: "Accès à l'Espace", free: 'Non', auto: 'Espace+' },
    { label: "Accès à l'Atelier", free: 'Non', auto: 'Oui' },
    { label: 'Accès aux demandes', free: 'Avec 3h de délai', auto: 'Oui' },
    {
      label: 'Priorité sur les demandes',
      free: 'Non',
      auto: 'Priorité renforcée',
      bold: true,
    },
    {
      label: 'Rendez-vous directs',
      free: 'Non',
      auto: 'Oui',
      info: 'Les prospects prennent RDV directement avec vous.',
    },
    { label: 'Notifications', free: 'Non', auto: 'Oui' },
    {
      label: 'Contribution aux campagnes communes',
      free: 'Non',
      auto: 'Contribution renforcée',
    },
  ];
  return (
    <Box
      sx={{
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        bgcolor: '#fff',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '200px 130px 150px', md: '1.6fr 1fr 1fr' },
          minWidth: { xs: 480, md: 'auto' },
        }}
      >
        <Cell header sticky />
        <Cell header>Découverte</Cell>
        <Cell header highlight>
          Autopilote
        </Cell>
        {rows.map((row, idx) => (
          <RowCells
            key={row.label}
            row={row}
            last={idx === rows.length - 1}
            onRdvInfo={onRdvInfo}
          />
        ))}
      </Box>
    </Box>
  );
};

const DemoHeader = ({
  number,
  title,
  subtitle,
}: {
  number: number;
  title: string;
  subtitle?: string;
}) => (
  <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: subtitle ? 1 : 0,
        justifyContent: { xs: 'flex-start', md: 'center' },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: PRIMARY,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography
        sx={{
          fontSize: { xs: '1.1rem', md: '1.4rem' },
          fontWeight: 700,
          color: '#1a1a1a',
        }}
      >
        {title}
      </Typography>
    </Box>
    {subtitle && (
      <Typography
        sx={{
          fontSize: 14,
          color: 'text.secondary',
          maxWidth: 640,
          mx: { xs: 0, md: 'auto' },
          lineHeight: 1.5,
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

interface CtaConfig {
  label: string;
  onClick: () => void;
  engagementText?: string;
}

interface HowItWorksDemoProps {
  showComparison?: boolean;
  cta?: CtaConfig;
}

// Les 2 méthodes (rappel prospect + RDV direct). Réutilisé par la LP et par la
// popup "Comment ça marche" de /pro. Affichage direct du résultat final.
export const HowItWorksMethods = () => (
  <>
    <Box sx={{ mb: 8 }}>
      <DemoHeader
        number={1}
        title="1ère méthode : Vous rappelez des prospects intéressés"
        subtitle="Certaines personnes cherchent une séance, souhaitent être rappelées, posent une question ou veulent en savoir plus avant de réserver."
      />
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5, mb: 0 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1.4,
            py: 0.5,
            borderRadius: 50,
            bgcolor: `${ACCENT_GREEN}14`,
            color: ACCENT_GREEN,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 14 }} />
          Transmise en priorité aux abonnées selon le secteur et la formule
        </Box>
      </Box>
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          bgcolor: '#fafafa',
          borderRadius: 4,
          border: '1px solid #f1f5f9',
          mt: 2,
        }}
      >
        <Demo1 />
      </Box>
    </Box>

    <Box sx={{ mb: 8 }}>
      <DemoHeader
        number={2}
        title="2ème méthode : Le client prend directement RDV avec vous"
        subtitle="Dans certains cas, la personne peut aussi prendre rendez-vous directement avec vous."
      />
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5, mb: 4 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1.4,
            py: 0.5,
            borderRadius: 50,
            bgcolor: `${ACCENT_GREEN}14`,
            color: ACCENT_GREEN,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 14 }} />
          Disponible seulement sur la formule &quot;Autopilote&quot;
        </Box>
      </Box>
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          bgcolor: '#fafafa',
          borderRadius: 4,
          border: '1px solid #f1f5f9',
        }}
      >
        <Demo2 />
      </Box>
    </Box>
  </>
);

// Bloc engagement + bouton CTA (partagé entre la LP et la section formules de /pro).
const EngagementCta = ({ cta }: { cta?: CtaConfig }) => {
  const engagementText =
    cta?.engagementText ??
    'Facturé chaque mois, engagement 3 ou 12 mois. Résiliation en 2 clics, accès gardé jusqu’à la fin de la période d’engagement.';
  const ctaLabel = cta?.label ?? 'Choisir ma formule';
  const ctaOnClick =
    cta?.onClick ??
    (() => {
      document.getElementById('autopilot-plan')?.scrollIntoView({ behavior: 'smooth' });
    });

  return (
    <Box sx={{ textAlign: 'center', mb: 4 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          color: ACCENT_GREEN,
          fontWeight: 600,
          mb: 3,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 20 }} />
        <Typography sx={{ fontSize: 14 }}>{engagementText}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Button
          variant="contained"
          size="large"
          onClick={(e) => {
            e.preventDefault();
            ctaOnClick();
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            py: 1.5,
            px: 4,
            fontSize: 16,
          }}
        >
          {ctaLabel}
        </Button>
      </Box>
    </Box>
  );
};

// Section standalone "Détails des formules" : tableau comparatif + engagement + CTA.
// Utilisé en section de niveau supérieur sur /pro.
export const FormulesSection = ({
  cta,
  onRdvInfo,
}: { cta?: CtaConfig; onRdvInfo?: () => void } = {}) => (
  <Box id="details-formules" sx={{ mt: { xs: 8, md: 12 }, scrollMarginTop: 80 }}>
    <Box sx={{ textAlign: 'center', mb: 6, maxWidth: 720, mx: 'auto', px: 2 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '1.6rem', md: '2.4rem' },
          mb: 2,
          color: '#1a1a1a',
        }}
      >
        Détails des formules
      </Typography>
    </Box>
    <Box sx={{ mb: 8 }}>
      <ComparisonTable onRdvInfo={onRdvInfo} />
    </Box>
    <EngagementCta cta={cta} />
  </Box>
);

// Composant complet "Comment ça marche ?" (titre + méthodes + comparatif optionnel
// + CTA). Conservé pour la landing page (LpSharedSections).
const HowItWorksDemo = ({ showComparison = true, cta }: HowItWorksDemoProps = {}) => (
  <Box id="comment-ca-marche" sx={{ mt: { xs: 8, md: 12 }, scrollMarginTop: 80 }}>
    <Box sx={{ textAlign: 'center', mb: 6, maxWidth: 720, mx: 'auto', px: 2 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '1.6rem', md: '2.4rem' },
          mb: 2,
          color: '#1a1a1a',
        }}
      >
        Comment ça marche ?
      </Typography>
    </Box>

    <HowItWorksMethods />

    {showComparison && (
      <Box id="details-formules" sx={{ mb: 8, scrollMarginTop: 80 }}>
        <DemoHeader number={3} title="Détails des formules" />
        <Box sx={{ mt: 4 }}>
          <ComparisonTable />
        </Box>
      </Box>
    )}

    <EngagementCta cta={cta} />
  </Box>
);

export default HowItWorksDemo;
